import { FiZap } from 'react-icons/fi';

export const speedReadingComplexData = {
  courseId: 'speed-reading-complex',
  courseType: 'reading-course',
  title: 'Speed Reading Complex Texts',
  icon: FiZap,
  overview: {
    impact: 'Handle dense academic paragraphs without losing structure',
    focus: 'Chunking, selective attention, and variable reading speed',
    uses: 'Reading time management and complex passage control',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What Makes a Passage Feel Dense',
      content:
        '<p>Complex texts become slow when they contain long noun phrases, embedded clauses, unfamiliar terminology, and frequent comparisons.</p>',
      points: [
        'Dense writing often hides the main verb far into the sentence.',
        'Academic passages stack ideas using commas, dashes, and relative clauses.',
        'You do not need equal attention on every word to understand the structure.',
      ],
    },
    {
      title: 'Read in Meaning Chunks',
      content:
        '<p>Instead of reading one word at a time, group words into short units of meaning.</p>',
      examples: [
        {
          label: 'Slow reading',
          text: 'The / rapid / expansion / of / suburban / rail / systems / transformed / commuting.',
        },
        {
          label: 'Chunked reading',
          text: 'The rapid expansion / of suburban rail systems / transformed commuting.',
        },
      ],
      points: [
        'Chunks reduce eye stops and improve speed.',
        'The goal is not speed alone; the goal is faster structure recognition.',
      ],
    },
    {
      title: 'Change Speed Intelligently',
      content: '<p>Good readers do not read every sentence at the same speed.</p>',
      table: {
        headers: ['When to Slow Down', 'When to Speed Up'],
        rows: [
          ['A sentence contains the answer area', 'A sentence gives background only'],
          ['A comparison or conclusion appears', 'Examples repeat the same idea'],
          ['A question asks about writer view or detail', 'You are skimming for paragraph role'],
        ],
      },
    },
    {
      title: 'A Practical Dense-Text Workflow',
      content: '<p>When a passage feels difficult, use this routine:</p>',
      points: [
        'Skim the first sentence and final sentence of the paragraph for direction.',
        'Mark the subject, the main verb, and any contrast signal such as however or although.',
        'Ignore non-essential detail until you know the paragraph\'s job.',
        'Return for precise reading only when a question sends you back.',
      ],
      warning:
        'Trying to understand every word immediately often slows you down more than necessary.',
    },
  ],
  exercises: [
    {
      title: 'Choose the Best Chunking',
      type: 'select',
      instructions: 'Choose the version that groups the sentence most helpfully.',
      questions: [
        {
          text:
            'Sentence: "The gradual decline in insect populations has alarmed many agricultural researchers."\nBest chunking:',
          options: [
            'The gradual decline / in insect populations / has alarmed / many agricultural researchers.',
            'The / gradual / decline in / insect / populations has / alarmed many / agricultural researchers.',
            'The gradual / decline in insect / populations has alarmed many agricultural / researchers.',
          ],
          answer:
            'The gradual decline / in insect populations / has alarmed / many agricultural researchers.',
          explanation:
            'This grouping preserves natural phrase boundaries and makes the main verb easy to spot.',
        },
        {
          text:
            'Sentence: "Although the results were limited, the review identified several useful patterns."\nBest reading decision:',
          options: [
            'Slow down at "Although" because it signals a contrast.',
            'Ignore the first clause completely.',
            'Read every word at the same speed.',
          ],
          answer: 'Slow down at "Although" because it signals a contrast.',
          explanation:
            'Contrast markers often reveal the key relationship in the sentence.',
        },
      ],
    },
    {
      title: 'Find the Main Structure',
      type: 'select',
      instructions: 'Choose the best description of the sentence skeleton.',
      questions: [
        {
          text:
            'Sentence: "The policy, despite strong support from local officials, failed to gain national approval."\nMain structure:',
          options: [
            'The policy failed to gain national approval.',
            'Local officials failed.',
            'National approval supported local officials.',
          ],
          answer: 'The policy failed to gain national approval.',
          explanation:
            'The inserted phrase adds context, but the main clause is about the policy failing to gain approval.',
        },
        {
          text:
            'Sentence: "Researchers who had previously dismissed the method later admitted that it produced reliable data."\nMain structure:',
          options: [
            'Researchers later admitted that it produced reliable data.',
            'The method dismissed researchers.',
            'Reliable data later produced researchers.',
          ],
          answer: 'Researchers later admitted that it produced reliable data.',
          explanation:
            'Ignore the relative clause and focus on the main subject and verb.',
        },
      ],
    },
    {
      title: 'Type the Reading Priority',
      type: 'fill-in',
      instructions: 'Type one word: slow or fast.',
      questions: [
        {
          text:
            'You are skimming a paragraph only to identify its general role before matching headings. Reading speed:',
          answer: 'fast',
          explanation:
            'This is a structure-level task, so skimming speed is appropriate.',
        },
        {
          text:
            'You have located the exact sentence likely to contain the answer to a detail question. Reading speed:',
          answer: 'slow',
          explanation:
            'Once you reach the answer zone, accuracy matters more than speed.',
        },
        {
          text:
            'You are moving through a long example sequence after already understanding the paragraph\'s main idea. Reading speed:',
          answer: 'fast',
          explanation:
            'Examples can often be read more selectively after the main point is clear.',
        },
      ],
    },
    {
      title: 'Describe Your Dense-Text Strategy',
      type: 'writing',
      instructions: 'Explain how you would read the sentence or paragraph efficiently.',
      questions: [
        {
          text:
            'A paragraph contains many unfamiliar scientific terms, but your question asks only for the writer\'s main conclusion. How would you read it efficiently?',
          example:
            'I would identify the topic sentence, any contrast markers, and the concluding sentence first. I would not stop at every technical term unless it seems essential to the conclusion.',
          tip:
            'A good answer explains what you will ignore as well as what you will focus on.',
        },
      ],
    },
  ],
  tips: [
    'Look for sentence skeletons: subject, main verb, and key contrast.',
    'Chunk noun phrases and prepositional phrases together instead of reading word by word.',
    'Read faster through background detail and slower through answer zones.',
    'Do not panic when a sentence is long; simplify its structure first.',
  ],
  dailyPractice: [
    'Take one long sentence and mark its chunks with slashes.',
    'Practice finding the main clause in dense academic writing.',
    'Read one paragraph twice: first for structure, then for detail.',
    'Time yourself skimming introductions and conclusions of reading passages.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'Speed Reading Complex Texts: chunking dense language, controlling reading speed, and extracting structure quickly in advanced IELTS passages.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...speedReadingComplexData,
      title: speedReadingComplexData.title + ' (Foundations)',
      overview: { ...speedReadingComplexData.overview, duration: '15-20 minutes' },
      sections: speedReadingComplexData.sections.slice(0, 2),
      exercises: [
        {
          ...speedReadingComplexData.exercises[0],
          title: 'Choose the Best Chunking (Basic)',
          questions: speedReadingComplexData.exercises[0].questions.slice(0, 1),
        },
        {
          ...speedReadingComplexData.exercises[2],
          title: 'Type the Reading Priority (Basic)',
          questions: speedReadingComplexData.exercises[2].questions.slice(0, 2),
        },
      ],
    };
  }
  if (difficulty >= 4) {
    return {
      ...speedReadingComplexData,
      title: speedReadingComplexData.title + ' (Advanced)',
      overview: { ...speedReadingComplexData.overview, duration: '30-35 minutes' },
      sections: [
        ...speedReadingComplexData.sections,
        {
          title: 'Advanced Compression Skills',
          content:
            '<p>High-level readers mentally compress dense sentences into a short internal summary before moving on.</p>',
          points: [
            'Reduce each long sentence to 4-6 core words in your mind.',
            'Notice which clauses are central and which are supporting detail.',
            'Use punctuation to predict where meaning units begin and end.',
          ],
        },
      ],
      exercises: [
        ...speedReadingComplexData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Compression Drill',
          type: 'writing',
          instructions: 'Rewrite the sentence as a short core summary.',
          questions: [
            {
              text:
                'Sentence: "The review, while acknowledging several methodological weaknesses, concludes that the intervention still produced measurable gains in literacy."\nWrite the core message in 6-10 words.',
              example:
                'Despite weaknesses, the intervention improved literacy.',
              tip:
                'Keep the central claim and drop the non-essential framing.',
            },
          ],
        },
      ],
      tips: [
        ...(speedReadingComplexData.tips || []),
        'At Band 8+, simplify long sentences into mental headlines before answering questions.',
        'If a paragraph is dense, map structure first and detail second.',
      ],
    };
  }
  return speedReadingComplexData;
}
